import type { PDPClickEventProps } from '@/scopes/products/infrastructure/analytics'
import { useCMSPageField } from '@/cms/fields'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { HeadingLabel } from '@/scopes/products/pages/components/HeadingLabel'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, IconGlyph, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import FacilityType from './FacilityType'

const GeneralFacilitiesContainer = styled.div`
  background-color: ${tok('semantics_views_tinted_background_color')};
  margin: ${space('primitives_space_12')} 0;
  padding: ${space('primitives_space_16')};
  border-radius: ${tokPx('semantics_focus_outer_ring_corner_radius_s')};
`

const FacilitySection = styled.section.attrs({ tabIndex: 0 })`
  padding-bottom: ${space('primitives_space_24')};
  &:not(:last-of-type) {
    border-bottom: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_boxes_border_color')};
  }
  ${focusOutline()}
`

const SheetContent = styled(Sheet.Content)`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_24')};
`

const FacilityTitle = styled.div`
  display: flex;
  gap: ${space('primitives_space_8')};
  margin-bottom: ${space('primitives_space_12')};
`

const StyledButton = styled(Button)`
  width: 100%;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    width: fit-content;
  }
`

const GENERAL_FACILITIES_MAX_ITEMS = 8 as const

export default function Facilities() {
  const intl = useIntl()
  const facilities = useCMSPageField('PDP.facilities')
  const { sendEvent } = useAnalytics()

  const openSheetEvent: PDPClickEventProps = {
    element: 'Bottom sheet',
    description: 'Facilities bottom sheet opened',
    result: 'Facilities bottom sheet opened',
  }
  const closeSheetEvent: PDPClickEventProps = {
    element: 'Bottom sheet',
    description: 'Facilities bottom sheet closed',
    result: 'Facilities bottom sheet closed',
  }

  const handleChange = (isOpen: boolean) => {
    if (isOpen) {
      sendEvent(createPDPClickEvent(openSheetEvent))
    }
    else {
      sendEvent(createPDPClickEvent(closeSheetEvent))
    }
  }

  return (
    <>
      <HeadingLabel text={intl.formatMessage({ id: 'practical_info_facilities_title' })} />
      <GeneralFacilitiesContainer>
        <FacilityType facilityType={facilities[0]} maxItems={GENERAL_FACILITIES_MAX_ITEMS} />
      </GeneralFacilitiesContainer>
      <Sheet.Root onOpenChange={handleChange}>
        <Sheet.Trigger>
          <StyledButton variant="outlinedPrimary" size="M">
            {intl.formatMessage({ id: 'practical_info_facilities_cta' })}
          </StyledButton>
        </Sheet.Trigger>
        <Sheet.Body side="bottom">
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'practical_info_facilities_title' })}
          />
          <SheetContent>
            {facilities.map(
              facility =>
                facility.show && (
                  <FacilitySection key={facility.id}>
                    <FacilityTitle>
                      {facility.icon && <Icon size={24} glyph={facility.icon as IconGlyph} />}
                      <Typography variant="display-h-regular" forwardedAs="h3">
                        {intl.formatMessage({ id: facility.title })}
                      </Typography>
                    </FacilityTitle>
                    <FacilityType key={facility.id} facilityType={facility} />
                  </FacilitySection>
                ),
            )}
          </SheetContent>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}
